import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, XCircle, ArrowRight, Package } from "lucide-react";
import { getPaymentStatus, eur } from "@/lib/api";
import { useCart } from "@/lib/cart";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState("error");
          return;
        }
      } catch {
        if (attempts > 8) {
          setState("error");
          return;
        }
      }
      if (attempts >= 12) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 1800);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="bg-ink min-h-screen pt-[110px] md:pt-[130px] pb-28 flex items-center">
      <div className="container-x max-w-xl mx-auto text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-14 h-14 text-gold animate-spin mx-auto" />
            <h1 className="font-heading text-3xl uppercase mt-8">Zahlung wird geprüft …</h1>
            <p className="text-white/50 mt-3">Einen Moment bitte, wir bestätigen deine Bestellung.</p>
          </div>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="payment-success">
            <div className="h-20 w-20 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto">
              <Check className="w-10 h-10 text-gold" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase mt-8">Danke für deine Bestellung!</h1>
            <p className="text-white/60 mt-4">Deine Zahlung war erfolgreich. Du erhältst eine Bestätigung – wir machen deine STARSOX versandfertig.</p>

            {order && (
              <div className="mt-8 card-hair p-6 text-left">
                <div className="flex items-center gap-2 mb-4 text-white/50 font-mono text-[11px] uppercase tracking-widest"><Package className="w-4 h-4 text-gold" /> Bestellübersicht</div>
                {(order.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-white/10 text-sm">
                    <span className="text-white/70">STARSOX Grip Socks · {it.label} × {it.quantity}</span>
                    <span>{eur(it.quantity * (it.unit_price_cents || 999))}</span>
                  </div>
                ))}
                {(order.discount_cents || 0) > 0 && (
                  <div className="flex justify-between pt-3 text-sm" data-testid="success-team-discount">
                    <span className="text-gold">Teamrabatt (10 %)</span>
                    <span className="text-gold">−{eur(order.discount_cents)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-4 font-semibold">
                  <span>Gesamt</span>
                  <span>{eur(order.amount_cents || 0)}</span>
                </div>
              </div>
            )}

            <Link to="/shop" data-testid="success-continue" className="btn-primary mt-8">Weiter shoppen <ArrowRight className="w-4 h-4" /></Link>
          </motion.div>
        )}

        {state === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 className="w-14 h-14 text-gold animate-spin mx-auto" />
            <h1 className="font-heading text-3xl uppercase mt-8">Zahlung in Bearbeitung</h1>
            <p className="text-white/50 mt-3">Deine Zahlung wird noch verarbeitet. Sobald sie bestätigt ist, erhältst du eine Nachricht.</p>
            <Link to="/" className="btn-ghost mt-8">Zur Startseite</Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="payment-error">
            <XCircle className="w-14 h-14 text-white/40 mx-auto" />
            <h1 className="font-heading text-3xl uppercase mt-8">Etwas ist schiefgelaufen</h1>
            <p className="text-white/50 mt-3">Wir konnten den Zahlungsstatus nicht bestätigen. Bitte kontaktiere uns, falls Beträge abgebucht wurden.</p>
            <Link to="/" className="btn-ghost mt-8">Zur Startseite</Link>
          </div>
        )}
      </div>
    </div>
  );
}
